import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Fade,
  ButtonGroup,
  Button,
  FormHelperText,
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import ConditionalRender from '../common/ConditionalRender';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const defaultUiState = {
  hasAnsweredPartnerQuestion: false,
  hasAnsweredRetirementQuestion: false,
  hasAnsweredPartnerRetirementQuestion: false,
  hasAnsweredSocialSecurityQuestion: false,
  hasAnsweredPartnerSocialSecurityQuestion: false,
  hasAnsweredGoalsQuestion: false
};

const PersonalInfoStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { personal, uiState = defaultUiState } = wizardData;
  const [dobError, setDobError] = useState('');
  const [partnerDobError, setPartnerDobError] = useState('');
  
  // Initialize progress tracking
  const { trackFieldChange, trackValidationError } = useProgressTracking({
    stepName: 'Personal Information',
    stepNumber: 1
  });

  // Get today's date in YYYY-MM-DD format for max date validation
  const today = new Date().toISOString().split('T')[0];
  
  // Get date 100 years ago for min date validation (realistic age limit)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  const minDateStr = minDate.toISOString().split('T')[0];

  const validateDob = (dob: string): boolean => {
    if (!dob) {
      setDobError('Date of birth is required');
      return false;
    }
    
    const dobDate = new Date(dob);
    const currentDate = new Date();
    
    if (dobDate > currentDate) {
      setDobError('Date of birth cannot be in the future');
      return false;
    }
    
    // Check if person is at least 18 years old
    const eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(currentDate.getFullYear() - 18);
    
    if (dobDate > eighteenYearsAgo) {
      setDobError('You must be at least 18 years old');
      return false;
    }
    
    // Check if person is younger than 100 years
    const hundredYearsAgo = new Date();
    hundredYearsAgo.setFullYear(currentDate.getFullYear() - 100);
    
    if (dobDate < hundredYearsAgo) {
      setDobError('Please check the date of birth');
      return false;
    }
    
    setDobError('');
    return true;
  };

  const validatePartnerDob = (dob: string): boolean => {
    if (!dob) {
      setPartnerDobError('Date of birth is required');
      return false;
    }
    
    const dobDate = new Date(dob);
    const currentDate = new Date();
    
    if (dobDate > currentDate) {
      setPartnerDobError('Date of birth cannot be in the future');
      return false;
    }
    
    // Check if person is at least 18 years old
    const eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(currentDate.getFullYear() - 18);
    
    if (dobDate > eighteenYearsAgo) {
      setPartnerDobError('Your partner must be at least 18 years old');
      return false;
    }
    
    // Check if person is younger than 100 years
    const hundredYearsAgo = new Date();
    hundredYearsAgo.setFullYear(currentDate.getFullYear() - 100);
    
    if (dobDate < hundredYearsAgo) {
      setPartnerDobError('Please check the date of birth');
      return false;
    }
    
    setPartnerDobError('');
    return true;
  };

  // Validate DOB if it exists when component mounts
  useEffect(() => {
    if (personal.client1.dateOfBirth) {
      validateDob(personal.client1.dateOfBirth);
    }
    if (personal.client2.dateOfBirth) {
      validatePartnerDob(personal.client2.dateOfBirth);
    }
  }, []);

  const handleClient1Change = (field: 'firstName' | 'lastName' | 'dateOfBirth') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateWizardData({
      personal: {
        ...personal,
        client1: {
          ...personal.client1,
          [field]: event.target.value,
        },
      },
    });

    if (field === 'dateOfBirth') {
      validateDob(event.target.value);
    }
  };

  const handleClient2Change = (field: 'firstName' | 'lastName' | 'dateOfBirth') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateWizardData({
      personal: {
        ...personal,
        client2: {
          ...personal.client2,
          [field]: event.target.value,
        },
      },
    });

    if (field === 'dateOfBirth') {
      validatePartnerDob(event.target.value);
    }
  };

  const handleContactChange = (field: 'email' | 'phone') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateWizardData({
      personal: {
        ...personal,
        contact: {
          ...personal.contact,
          [field]: event.target.value,
        },
      },
    });
  };

  const handlePartnerChange = (hasPartner: boolean) => {
    updateWizardData({
      personal: {
        ...personal,
        hasPartner,
      },
      uiState: {
        ...uiState,
        hasAnsweredPartnerQuestion: true,
      },
    });
  };

  const hasName = personal.client1.firstName.trim() !== '' && personal.client1.lastName.trim() !== '';
  const hasDOB = personal.client1.dateOfBirth !== '' && !dobError;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Let's Get to Know You
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box>
          <Typography variant="body1" paragraph>
            Please introduce yourself. What's your name?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="First Name"
                value={personal.client1.firstName}
                onChange={handleClient1Change('firstName')}
                placeholder="Enter your first name"
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="Last Name"
                value={personal.client1.lastName}
                onChange={handleClient1Change('lastName')}
                placeholder="Enter your last name"
                required
              />
            </Box>
          </Box>
        </Box>

        <ConditionalRender show={hasName}>
          <Box>
            <Typography variant="body1" paragraph>
              Nice to meet you {personal.client1.firstName}! When were you born? This helps us understand your retirement timeline and plan accordingly.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={personal.client1.dateOfBirth}
                  onChange={handleClient1Change('dateOfBirth')}
                  error={!!dobError}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: today,
                    min: minDateStr,
                  }}
                  required
                />
                {dobError && (
                  <FormHelperText error>{dobError}</FormHelperText>
                )}
              </Box>
            </Box>
          </Box>
        </ConditionalRender>

        <ConditionalRender show={hasDOB}>
          <Box>
            <Typography variant="body1" paragraph>
              Are you planning for retirement with a partner?
            </Typography>
            <ButtonGroup variant="contained" sx={{ mt: 2 }}>
              <Button
                onClick={() => handlePartnerChange(true)}
                color={uiState.hasAnsweredPartnerQuestion && personal.hasPartner === true ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: uiState.hasAnsweredPartnerQuestion && personal.hasPartner === true ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: uiState.hasAnsweredPartnerQuestion && personal.hasPartner === true ? undefined : 'grey.400',
                  }
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  updateWizardData({
                    personal: {
                      ...personal,
                      hasPartner: false,
                      client2: {
                        firstName: '',
                        lastName: '',
                        dateOfBirth: '',
                      },
                    },
                    uiState: {
                      ...uiState,
                      hasAnsweredPartnerQuestion: true,
                    }
                  });
                }}
                color={uiState.hasAnsweredPartnerQuestion && personal.hasPartner === false ? 'primary' : 'inherit'}
                sx={{
                  minWidth: '120px',
                  backgroundColor: uiState.hasAnsweredPartnerQuestion && personal.hasPartner === false ? undefined : 'grey.300',
                  '&:hover': {
                    backgroundColor: uiState.hasAnsweredPartnerQuestion && personal.hasPartner === false ? undefined : 'grey.400',
                  }
                }}
              >
                No
              </Button>
            </ButtonGroup>
          </Box>
        </ConditionalRender>

        <ConditionalRender show={personal.hasPartner}>
          <Box>
            <Typography variant="body1" paragraph>
              Great! Let's get to know your partner as well.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="Partner's First Name"
                  value={personal.client2.firstName}
                  onChange={handleClient2Change('firstName')}
                  placeholder="Enter partner's first name"
                  required
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="Partner's Last Name"
                  value={personal.client2.lastName}
                  onChange={handleClient2Change('lastName')}
                  placeholder="Enter partner's last name"
                  required
                />
              </Box>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" paragraph>
                When was your partner born?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label="Partner's Date of Birth"
                    type="date"
                    value={personal.client2.dateOfBirth}
                    onChange={handleClient2Change('dateOfBirth')}
                    error={!!partnerDobError}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: today,
                      min: minDateStr,
                    }}
                    required
                  />
                  {partnerDobError && (
                    <FormHelperText error>{partnerDobError}</FormHelperText>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </ConditionalRender>

        <ConditionalRender
          show={Boolean(
            hasDOB &&
            uiState.hasAnsweredPartnerQuestion &&
            (
              // If "No" was selected for partner, show immediately
              (personal.hasPartner === false) ||
              // If "Yes" was selected, wait for partner name and DOB
              (personal.hasPartner === true &&
               personal.client2.firstName.trim() !== '' &&
               personal.client2.lastName.trim() !== '' &&
               personal.client2.dateOfBirth !== '' && !partnerDobError)
            )
          )}
        >
          <Box>
            <Typography variant="body1" paragraph>
              Great! Let's move on to the next step.
            </Typography>
          </Box>
        </ConditionalRender>
      </Box>
    </Box>
  );
};

export default PersonalInfoStep; 